<script lang="ts">
import { LEGAL_LINKS } from "@doc-cheap/legal";
import { OPERATOR_ADDRESS, OPERATOR_EMAIL, OPERATOR_NAME } from "$lib/seo/operator";
import SeoHead from "$lib/seo/SeoHead.svelte";
import SiteFooter from "$lib/seo/SiteFooter.svelte";
import { DOCS_URL } from "$lib/seo/site";

// How to reach the operator.
//
// An address rather than a form, deliberately. A form is a second thing to
// keep working, it loses the sender's own copy of what they wrote, and it
// cannot carry the attachment that makes a recognition problem diagnosable.
// What a page like this is actually for is telling somebody what to put in the
// message so the first reply is the useful one.

const title = "Contact doc.cheap – support, legal and privacy requests";
const description =
  `Write to ${OPERATOR_EMAIL} for API support, billing, legal notices and privacy requests, ` +
  "with what to include so the first reply is the useful one.";
</script>

<SeoHead {title} {description} path="/contact" />

<div>
  <main class="mx-auto max-w-3xl px-6 py-12">
    <h1 class="text-3xl font-semibold tracking-tight sm:text-4xl">Contact</h1>

    <p class="mt-5 text-lg text-slate-600">
      One address, for everything:
      <a href="mailto:{OPERATOR_EMAIL}" class="font-medium underline hover:text-slate-900"
        >{OPERATOR_EMAIL}</a
      >. It is read by the people who build the service, not by a queue in front of them.
    </p>

    <section class="mt-12" aria-labelledby="what-to-include">
      <h2 id="what-to-include" class="text-2xl font-semibold tracking-tight">What to include</h2>
      <dl class="mt-4 space-y-5 text-slate-600">
        <div>
          <dt class="font-medium text-slate-900">A recognition that went wrong</dt>
          <dd class="mt-1">
            The scan id from the response and roughly when you made the call. That is enough to find
            it. Please do not attach the document image: we do not keep it, and we do not want a
            copy of somebody's passport in a mailbox.
          </dd>
        </div>
        <div>
          <dt class="font-medium text-slate-900">Billing</dt>
          <dd class="mt-1">
            The email the account is registered under, and the date of the charge or top-up you are
            asking about. Every call's outcome and whether it was billed are already in the
            dashboard, and a scan that found nothing is never charged.
          </dd>
        </div>
        <div>
          <dt class="font-medium text-slate-900">A privacy request</dt>
          <dd class="mt-1">
            Say which account it concerns and what you want done. What the service stores, for how
            long, and how to delete it is set out in the
            <a href="/privacy" class="underline hover:text-slate-900">Privacy Policy</a>.
          </dd>
        </div>
        <div>
          <dt class="font-medium text-slate-900">A legal notice</dt>
          <dd class="mt-1">
            The same address serves, and the registered postal address is below. The published
            texts are the whole agreement; we do not sign vendor forms, questionnaires or side
            letters.
          </dd>
        </div>
      </dl>
    </section>

    <section class="mt-12" aria-labelledby="before-you-write">
      <h2 id="before-you-write" class="text-2xl font-semibold tracking-tight">
        Faster than writing to us
      </h2>
      <ul class="mt-4 space-y-2 text-slate-700">
        <li>
          <a href="/status" class="underline hover:text-slate-900">Service status</a> – if something
          is failing right now, this page says whether it is us.
        </li>
        <li>
          <a href="{DOCS_URL}/reference/errors" rel="external" class="underline hover:text-slate-900"
            >Every error code</a
          > – each one with what causes it and what to do about it.
        </li>
        <li>
          <a href="{DOCS_URL}/start/first-recognition" rel="external" class="underline hover:text-slate-900"
            >Your first recognition</a
          > – the whole call, start to finish, with no account.
        </li>
      </ul>
    </section>

    <section class="mt-12" aria-labelledby="postal">
      <h2 id="postal" class="text-2xl font-semibold tracking-tight">Postal address</h2>
      <address class="mt-4 text-slate-700 not-italic">
        <strong class="font-semibold text-slate-900">{OPERATOR_NAME}</strong><br />
        {#each OPERATOR_ADDRESS as line (line)}
          {line}<br />
        {/each}
      </address>
      <p class="mt-4 text-sm text-slate-500">
        <a href="/about" class="underline hover:text-slate-900">About the operator</a>
        {#each LEGAL_LINKS as link (link.id)}
          <span aria-hidden="true"> · </span><a href={link.href} class="underline hover:text-slate-900"
            >{link.label}</a
          >
        {/each}
      </p>
    </section>
  </main>

  <SiteFooter />
</div>
